import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { rgba } from 'polished';

import { Colours, Sizes, ZIndex, CircleSpinner, trackHeapEvent, noticeError, Config, Products } from '@ratehub/base-ui';

import fetchSearchResults from '../functions/fetchSearchResults';
import mapCreditCardSearchToFeaturedProductListProps from '../functions/mapCreditCardSearchToFeaturedProductListProps';
import SearchForm from './SearchForm';
import FeaturedProductList from './FeaturedProductList';


/**
 * SearchResults Component
 *
 * Displays search results based on the URL query parameter `s`.
 * Handles loading, error, empty, and results states.
 * @example
 * // URL: /search?s=mortgage
 * <SearchResults />
 * @returns {React.ReactElement} The rendered search results component
 */
function SearchResults() {
    const intl = useIntl();
    const [ searchQuery, setSearchQuery ] = useState('');
    const [ results, setResults ] = useState([]);
    const [ productList, setProductList ] = useState([]);
    // Start with loading true to show spinner immediately on page load
    const [ isLoading, setIsLoading ] = useState(true);
    const [ error, setError ] = useState(null);
    const [ hasSearched, setHasSearched ] = useState(false);

    // Initialize search query from URL on mount
    useEffect(() => {
        const query = getSearchQueryFromUrl();
        setSearchQuery(query);

        if (!query) {
            setIsLoading(false);
            setHasSearched(true);
            setResults([]);
            setProductList([]);
            return;
        }

        performSearch(query);
    }, []);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const query = getSearchQueryFromUrl();
            setSearchQuery(query);

            if (query) {
                performSearch(query);
            } else {
                setHasSearched(true);
                setResults([]);
                setProductList([]);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    /**
     * Formats a date string into a human-readable format using intl.formatDate
     * @param {string} dateString - ISO date string or any valid date string
     * @returns {string} Formatted date string (e.g., "Jan 15, 2024") or original string if parsing fails
     */
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                return dateString;
            }

            return intl.formatDate(date, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return dateString;
        }
    };

    /**
     * Performs a search request to the API
     * Ensures minimum 500ms loading time for better UX
     * @param {string} query - The search query string
     * @returns {Promise<void>}
     */
    const performSearch = async (query) => {
        setIsLoading(true);
        setError(null);
        setResults([]);
        setProductList([]);
        setHasSearched(true);

        try {
            // Run both the API call and minimum delay in parallel
            const [ { results: searchResults, productList: searchProductList } ] = await Promise.all([
                fetchSearchResults(Config.SEARCH_API_URL, {
                    query: query,
                    page: 1,
                    perPage: 12,
                }),
                new Promise(resolve => setTimeout(resolve, 500)),
            ]);

            setResults(searchResults);
            setProductList(searchProductList);

            if (searchProductList.some(group => group?.products?.length > 0)) {
                trackHeapEvent('MBR - Search - Product Spotlight Appeared', {
                    searchQuery: query,
                    products: searchProductList,
                });
            }

            if (searchResults.length === 0) {
                trackHeapEvent('MBR - Search - No results', {
                    searchQuery: query,
                });
            }
        } catch (err) {
            noticeError(new Error(err.message || 'An error occurred. Please try again.'));
            setError(err.message || 'An error occurred. Please try again.');
            setResults([]);
            setProductList([]);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handles new search submissions by updating the URL and performing search
     * Uses History API to update URL without page reload
     * @param {string} query - The new search query string
     */
    const handleNewSearch = (query) => {
        // Update URL without reload
        const newUrl = `/search?s=${encodeURIComponent(query)}`;
        window.history.pushState({}, '', newUrl);

        // Update local state
        setSearchQuery(query);

        // Perform the search directly
        performSearch(query);
    };

    const noResultsAltText = intl.formatMessage(MESSAGES.noResultsImageAlt);

    return (
        <Container>
            <div className="search-form-wrapper rh-py-2 rh-px-1 rh-mb-0 rh-mx-auto">
                <SearchForm
                    onSearch={handleNewSearch}
                    initialQuery={searchQuery || ''}
                />
            </div>

            <div className="content-container">
                <If condition={isLoading}>
                    <div className="loading-container rh-position-relative rh-width-100p">
                        <CircleSpinner
                            position="absolute"
                            zIndex={ZIndex.ELEMENTS}
                            callerName="SearchResults"
                        />
                    </div>
                </If>

                <If condition={error && !isLoading}>
                    <div className="error-state rh-p-2 rh-text-align-center rh-display-flex rh-flex-direction-column rh-align-items-center rh-justify-content-center">
                        <img
                            src="/static/search-no-results.svg"
                            alt={noResultsAltText}
                            className="empty-state-image rh-mb-2"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <h2 className="rh-fg-blackberry rh-mb-1 rh-m-0">
                            <FormattedMessage {...MESSAGES.errorTitle} />
                        </h2>
                        <p className="rh-fg-blackberry-light rh-m-0">
                            <FormattedMessage {...MESSAGES.errorDescription} />
                        </p>
                    </div>
                </If>

                <If condition={!isLoading && !error && hasSearched}>
                    <Choose>
                        <When condition={results.length > 0}>
                            <section className="results-section rh-mx-auto rh-px-1">
                                <h2 className="results-header rh-fg-blackberry rh-m-0 rh-pt-0_5 rh-pb-0_5">
                                    <FormattedMessage {...MESSAGES.spotlightHeader} />
                                </h2>

                                <If condition={productList.some(group => group?.products?.length > 0)}>
                                    <div className="spotlight-section rh-mb-1">
                                        <FeaturedProductList
                                            products={mapCreditCardSearchToFeaturedProductListProps(productList)}
                                            productType={Products.CREDIT_CARDS}
                                            verifyProvince={false}
                                            showAdDisclosure={false}
                                        />
                                    </div>
                                </If>

                                <ul className="results-list rh-p-0 rh-pb-3 rh-m-0">
                                    <For
                                        each="result"
                                        of={results.slice(0, 12)}
                                        index="index"
                                    >
                                        <li
                                            key={index}
                                            className="result-item"
                                            data-name={`search-result-${index+1}`}
                                        >
                                            <a

                                                href={result.link}
                                                className="rh-display-block rh-text-decoration-none"
                                            >
                                                <h3 className="result-title rh-fg-blueberry-dark rh-pt-1 rh-mt-0 rh-mb-1">
                                                    {result.title}
                                                </h3>

                                                <If condition={result.description}>
                                                    <p className="result-description rh-fg-blackberry rh-mb-0_5">
                                                        {result.description}
                                                    </p>
                                                </If>

                                                <If condition={result.modifiedAt}>
                                                    <time className="result-date rh-display-block rh-mb-1">
                                                        <FormattedMessage
                                                            {...MESSAGES.lastUpdated}
                                                            values={{
                                                                date: formatDate(result.modifiedAt),
                                                            }}
                                                        />
                                                    </time>
                                                </If>
                                            </a>
                                        </li>
                                    </For>
                                </ul>
                            </section>
                        </When>
                        <Otherwise>
                            <div className="empty-state rh-p-2 rh-text-align-center rh-display-flex rh-flex-direction-column rh-align-items-center rh-justify-content-center">
                                <img
                                    src="/static/search-no-results.svg"
                                    alt={noResultsAltText}
                                    className="empty-state-image rh-mb-2"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <h2 className="rh-fg-blackberry rh-mb-1 rh-m-0">
                                    <FormattedMessage {...MESSAGES.noResultsTitle} />
                                </h2>
                                <p className="rh-fg-blackberry-light rh-m-0">
                                    <FormattedMessage {...MESSAGES.noResultsDescription} />
                                </p>
                            </div>
                        </Otherwise>
                    </Choose>
                </If>
            </div>
        </Container>
    );
}

const MESSAGES = defineMessages({
    errorTitle: {
        id: 'searchResults.errorTitle',
        defaultMessage: 'Ooops',
    },
    errorDescription: {
        id: 'searchResults.errorDescription',
        defaultMessage: 'Something went wrong try again later',
    },
    spotlightHeader: {
        id: 'searchResults.spotlightHeader',
        defaultMessage: 'Search results',
    },
    lastUpdated: {
        id: 'searchResults.lastUpdated',
        defaultMessage: 'Last updated: {date}',
    },
    noResultsTitle: {
        id: 'searchResults.noResultsTitle',
        defaultMessage: 'No results found',
    },
    noResultsDescription: {
        id: 'searchResults.noResultsDescription',
        defaultMessage: 'Try to adjust your search keyword or filter to find what you are looking for',
    },
    noResultsImageAlt: {
        id: 'searchResults.noResultsImageAlt',
        defaultMessage: 'No results',
    },
});

/**
 * Gets the search query from the URL
 * SSR-safe: returns empty string on server, actual query on client
 * @returns {string} The search query from URL parameter 's'
 */
const getSearchQueryFromUrl = () => {
    if (typeof window === 'undefined') {
        return '';
    }
    const params = new URLSearchParams(window.location.search);
    return params.get('s') || '';
};

const Container = styled.div`
    > .search-form-wrapper {
        background-color: ${Colours.GREY_100};
    }

    > .content-container {
        min-height: 28.57rem;
        position: relative;
        overflow-x: hidden;
    }

    > .content-container > .loading-container {
        min-height: 28.57rem;
    }

    > .content-container > .error-state {
        min-height: 28.57rem;
        padding: ${Sizes.SPACING.TWO};

        > h2 {
            font-size: ${Sizes.FONTS.L};
            font-weight: 400;
        }

        > p {
            max-width: 32.14rem;
            font-size: ${Sizes.FONTS.XS};
            line-height: ${Sizes.LINE_HEIGHT.M};
        }
    }

    > .content-container > .results-section > .spotlight-section {
        max-width: 1440px;

        > div > div {
            margin-top: 0;
        }

        .label {
            visibility: hidden;
        }

        .cta-target {
            grid-template-rows: ${Sizes.SPACING.ONE} auto 1fr auto;
            padding: ${Sizes.SPACING.HALF} ${Sizes.SPACING.ONE} ${Sizes.SPACING.ONE} !important;

            .cta-wrapper {
                flex-wrap: nowrap;
                flex-direction: row;

                .cc-cta {
                    flex: 1;
                    font-size: ${Sizes.FONTS.XS};
                }
            }

            .card-title {
                padding-bottom: ${Sizes.SPACING.HALF};
                font-size: ${Sizes.FONTS.S};
            }

            .product-content {
                img {
                    margin-top: ${Sizes.SPACING.QUARTER};
                    margin-bottom: ${Sizes.SPACING.QUARTER};
                }

                .description {
                    margin-top: ${Sizes.SPACING.HALF};
                    margin-bottom: ${Sizes.SPACING.HALF};
                }
            }
        }

        .carousel {
            align-items: flex-start;

            .carousel__child_container {
                align-items: stretch;
            }
        }

        .carousel__button-container {
            margin-top: ${Sizes.SPACING.ONE};
        }

        .heading-block {
            margin-bottom: ${Sizes.SPACING.ONE};

            h2 {
                font-size: ${Sizes.FONTS.L};
                font-weight: 500;
                line-height: ${Sizes.LINE_HEIGHT.XL};
                margin-bottom: 0;

                &::after {
                    display: none;
                }
            }
        }
    }

    > .content-container > .results-section {
        max-width: 1440px;

        > .results-header {
            font-weight: 500;
            font-size: ${Sizes.FONTS.L}; /* 1.25rem - 20px on desktop - title-small */
            line-height: ${Sizes.LINE_HEIGHT.XL};
        }

        > .results-list {
            list-style: none;
            font-size: 0;
            border-top: 1px solid ${Colours.STONE};

            > .result-item {
                border-bottom: 1px solid ${Colours.STONE};
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: ${rgba(Colours.BLUEBERRY_LIGHTEST, 0.4)};
                }

                > a {
                    > .result-title {
                        font-weight: 500;
                        font-size: ${Sizes.FONTS.S}; /* 1rem - 16px on desktop - title-medium */
                        line-height: ${Sizes.LINE_HEIGHT.M}; /* 1.571 → 1.5 */
                    }

                    > .result-description {
                        font-weight: 400;
                        font-size: ${Sizes.FONTS.S}; /* 1rem - 16px on desktop - text-medium */
                        line-height: ${Sizes.LINE_HEIGHT.M}; /* 1.571 → 1.5 */
                    }

                    > .result-date {
                        font-weight: 400;
                        font-size: ${Sizes.FONTS.XS}; /* 0.857rem → 0.875rem */
                        line-height: ${Sizes.LINE_HEIGHT.S}; /* 1.142 → 1.2 */
                        color: ${Colours.BLACKBERRY_LIGHT};
                    }
                }
            }
        }
    }

    > .content-container > .empty-state {
        min-height: 28.57rem;
        padding: ${Sizes.SPACING.TWO};

        > h2 {
            font-size: ${Sizes.FONTS.L};
            font-weight: 400;
        }

        > p {
            max-width: 32.14rem;
            font-size: ${Sizes.FONTS.XS};
            line-height: ${Sizes.LINE_HEIGHT.M}; /* 1.4 → 1.5 */
        }
    }

    .empty-state-image {
        max-width: 17.86rem;
        width: 100%;
        height: auto;
        margin-bottom: ${Sizes.SPACING.TWO};
    }
`;

export default SearchResults;
